import { ChevronLeft, ChevronRight, X } from 'lucide-react-native';
import { useEffect, useMemo, useState } from 'react';
import { Modal, Pressable, StyleSheet, Text, View } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';

/**
 * Cross-platform calendar grid date picker. Web doesn't get a native date
 * picker we can trust (and the input type="date" doesn't render inside
 * RN-Web TextInput), so we build a single component that works the same
 * everywhere. Month navigation + day grid; today is highlighted, selected
 * day gets the forest pill. Returns the chosen day at noon local time so
 * timezone offsets don't push it across day boundaries.
 *
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   <DatePickerSheet open={open} value={someMs} onPick={(ms) => ...} onClose={...} />
 */
const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

export function DatePickerSheet({
  open,
  value,
  onPick,
  onClose,
  title = 'Pick a date',
  minDate,
}: {
  open: boolean;
  value: number | null;
  onPick: (ms: number) => void;
  onClose: () => void;
  title?: string;
  minDate?: number;
}) {
  const initial = value ? new Date(value) : new Date();
  const [viewYear, setViewYear] = useState(initial.getFullYear());
  const [viewMonth, setViewMonth] = useState(initial.getMonth());

  useEffect(() => {
    if (!open) return;
    const d = value ? new Date(value) : new Date();
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  }, [open, value]);

  const anim = useSharedValue(0);
  useEffect(() => {
    anim.value = withTiming(open ? 1 : 0, {
      duration: 220,
      easing: Easing.out(Easing.cubic),
    });
  }, [open, anim]);

  const backdropStyle = useAnimatedStyle(() => ({ opacity: anim.value * 0.55 }));
  const sheetStyle = useAnimatedStyle(() => ({
    opacity: anim.value,
    transform: [{ translateY: (1 - anim.value) * 40 }],
  }));

  const today = useMemo(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  }, []);
  const selectedDayKey = value ? ymd(value) : null;

  const cells = useMemo(() => buildMonthGrid(viewYear, viewMonth), [viewYear, viewMonth]);

  const stepMonth = (delta: number) => {
    let m = viewMonth + delta;
    let y = viewYear;
    while (m < 0) { m += 12; y -= 1; }
    while (m > 11) { m -= 12; y += 1; }
    setViewMonth(m);
    setViewYear(y);
  };

  const monthLabel = new Date(viewYear, viewMonth, 1).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });

  return (
    <Modal animationType="none" transparent visible={open} onRequestClose={onClose}>
      <Animated.View style={[styles.backdrop, backdropStyle]}>
        <Pressable style={styles.backdropPress} onPress={onClose} />
      </Animated.View>
      <View pointerEvents="box-none" style={styles.wrap}>
        <Animated.View style={[styles.sheet, sheetStyle]}>
          <View style={styles.head}>
            <Text style={styles.title}>{title}</Text>
            <Pressable onPress={onClose} hitSlop={8} style={({ pressed }) => [styles.closeBtn, pressed && { opacity: 0.7 }]}>
              <X size={18} color={Palette.ink} strokeWidth={2} />
            </Pressable>
          </View>

          <View style={styles.monthHead}>
            <Pressable onPress={() => stepMonth(-1)} hitSlop={8} style={styles.navBtn}>
              <ChevronLeft size={18} color={Palette.ink} strokeWidth={2} />
            </Pressable>
            <Text style={styles.monthLabel}>{monthLabel}</Text>
            <Pressable onPress={() => stepMonth(1)} hitSlop={8} style={styles.navBtn}>
              <ChevronRight size={18} color={Palette.ink} strokeWidth={2} />
            </Pressable>
          </View>

          <View style={styles.weekRow}>
            {WEEKDAYS.map((w, i) => (
              <Text key={i} style={styles.weekHead}>{w}</Text>
            ))}
          </View>

          <View style={styles.grid}>
            {cells.map((cell, i) => {
              if (!cell) {
                return <View key={i} style={styles.cell} />;
              }
              const dayMs = new Date(viewYear, viewMonth, cell, 12, 0, 0).getTime();
              const key = ymd(dayMs);
              const isToday = ymd(today) === key;
              const isSelected = selectedDayKey === key;
              const isBeforeMin = minDate != null && dayMs < startOfDay(minDate);
              return (
                <Pressable
                  key={i}
                  onPress={() => {
                    if (isBeforeMin) return;
                    onPick(dayMs);
                  }}
                  disabled={isBeforeMin}
                  style={({ pressed }) => [
                    styles.cell,
                    pressed && !isBeforeMin && { opacity: 0.85 },
                  ]}>
                  <View
                    style={[
                      styles.cellInner,
                      isToday && !isSelected && styles.cellToday,
                      isSelected && styles.cellSelected,
                    ]}>
                    <Text
                      style={[
                        styles.cellText,
                        isToday && !isSelected && styles.cellTextToday,
                        isSelected && styles.cellTextSelected,
                        isBeforeMin && styles.cellTextDisabled,
                      ]}>
                      {cell}
                    </Text>
                  </View>
                </Pressable>
              );
            })}
          </View>

          <View style={styles.quickRow}>
            <Pressable
              onPress={() => onPick(today + 12 * 60 * 60 * 1000)}
              style={({ pressed }) => [styles.quickBtn, pressed && { opacity: 0.85 }]}>
              <Text style={styles.quickText}>Today</Text>
            </Pressable>
            <Pressable
              onPress={() => onPick(today + 36 * 60 * 60 * 1000)}
              style={({ pressed }) => [styles.quickBtn, pressed && { opacity: 0.85 }]}>
              <Text style={styles.quickText}>Tomorrow</Text>
            </Pressable>
            <Pressable
              onPress={() => onPick(today + (7 * 24 + 12) * 60 * 60 * 1000)}
              style={({ pressed }) => [styles.quickBtn, pressed && { opacity: 0.85 }]}>
              <Text style={styles.quickText}>+1 week</Text>
            </Pressable>
            <Pressable
              onPress={() => onPick(today + (30 * 24 + 12) * 60 * 60 * 1000)}
              style={({ pressed }) => [styles.quickBtn, pressed && { opacity: 0.85 }]}>
              <Text style={styles.quickText}>+1 month</Text>
            </Pressable>
          </View>
        </Animated.View>
      </View>
    </Modal>
  );
}

function ymd(ts: number): string {
  const d = new Date(ts);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function pad(n: number): string { return String(n).padStart(2, '0'); }
function startOfDay(ts: number): number {
  const d = new Date(ts);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function buildMonthGrid(year: number, month: number): (number | null)[] {
  const firstWeekday = new Date(year, month, 1).getDay(); // 0 = Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const out: (number | null)[] = [];
  for (let i = 0; i < firstWeekday; i++) out.push(null);
  for (let d = 1; d <= daysInMonth; d++) out.push(d);
  // Pad to a multiple of 7 so the grid has full rows.
  while (out.length % 7 !== 0) out.push(null);
  return out;
}

const styles = StyleSheet.create({
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(15, 24, 18, 1)',
  },
  backdropPress: { flex: 1 },
  wrap: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 16,
  },
  sheet: {
    width: '100%',
    maxWidth: 360,
    backgroundColor: Palette.sand,
    borderRadius: Radius['2xl'],
    padding: 16,
  },
  head: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
  },
  title: {
    fontFamily: Fonts.serifBold, fontSize: 17, color: Palette.ink, letterSpacing: -0.2,
  },
  closeBtn: {
    width: 30, height: 30, borderRadius: 15,
    backgroundColor: Palette.card,
    borderWidth: 1, borderColor: Palette.line,
    alignItems: 'center', justifyContent: 'center',
  },
  monthHead: {
    marginTop: 10,
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 4,
  },
  navBtn: {
    width: 30, height: 30, borderRadius: 15,
    alignItems: 'center', justifyContent: 'center',
  },
  monthLabel: {
    fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink,
  },
  weekRow: {
    marginTop: 8,
    flexDirection: 'row',
  },
  weekHead: {
    flex: 1, textAlign: 'center',
    fontFamily: Fonts.sansBold, fontSize: 11.5, color: Palette.inkFaint,
    letterSpacing: 0.4,
  },
  grid: {
    marginTop: 4,
    flexDirection: 'row', flexWrap: 'wrap',
  },
  cell: {
    width: `${100 / 7}%`,
    aspectRatio: 1,
    alignItems: 'center', justifyContent: 'center',
    padding: 2,
  },
  cellInner: {
    width: 34, height: 34, borderRadius: 17,
    alignItems: 'center', justifyContent: 'center',
  },
  cellToday: {
    borderWidth: 1.5, borderColor: Palette.forest,
  },
  cellSelected: { backgroundColor: Palette.forest },
  cellText: {
    fontFamily: Fonts.sansSemibold, fontSize: 13.5, color: Palette.ink,
  },
  cellTextToday: { color: Palette.forest, fontFamily: Fonts.sansBold },
  cellTextSelected: { color: Palette.cream, fontFamily: Fonts.sansBold },
  cellTextDisabled: { color: Palette.inkFaint, opacity: 0.4 },
  quickRow: {
    flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 12,
    justifyContent: 'center',
  },
  quickBtn: {
    paddingHorizontal: 10, paddingVertical: 6, borderRadius: 999,
    borderWidth: 1.5, borderColor: Palette.line, backgroundColor: Palette.card,
  },
  quickText: { fontFamily: Fonts.sansBold, fontSize: 12.5, color: Palette.ink },
});
